import { laptopMediumMax } from '@propertypal/shared/src/constants/mediaQueries';
import LandingHero from '@propertypal/shared/src/resources/images/co-ownership/landing-hero.webp';
import platform from '@propertypal/shared/src/utils/platform';
import { MAX_WIDTH } from '@propertypal/web-ui/src/layout/PageWrapper.style';
import styled, { css } from 'styled-components';

export const RED = '#e83a4f';

export const Header = styled.div`
  background-color: ${RED};
  overflow: hidden;
`;

export const GridWrapper = styled.div`
  max-width: ${MAX_WIDTH}px;
  margin: 60px auto;
  padding: 0 10px;
  color: ${({ theme }) => theme.textDark};

  & > h2 {
    padding: 0 20px;
    font-size: 36px;
    font-family: ${platform.headingFont};

    b {
      color: ${RED};
    }
  }

  & > h4 {
    font-size: 24px;
    padding: 0 20px;
    margin: 15px 0;
    font-weight: normal;

    a {
      text-decoration: underline;
      color: ${RED};

      &:hover {
        color: ${({ theme }) => theme.backgroundMid};
      }
    }
  }

  & > p {
    padding: 0 20px;
    margin: 5px 0 50px;
    font-size: 16px;

    a {
      text-decoration: underline;
      color: ${RED};

      &:hover {
        color: ${({ theme }) => theme.backgroundMid};
      }
    }
  }

  @media (${laptopMediumMax}) {
    & > h2,
    & > h4,
    & > p {
      padding: 0 5px;
    }
  }
`;

export const HeaderContent = styled.div`
  display: flex;
  margin: 0 auto;
  max-width: ${MAX_WIDTH}px;

  @media (${laptopMediumMax}) {
    flex-direction: column;
  }
`;

export const HeaderImage = styled.div`
  flex: 1;
  position: relative;
`;

export const HeaderImageContent = styled.div`
  background-image: url(${LandingHero.src});
  background-size: cover;
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: calc(100vw / 2);

  @media (${laptopMediumMax}) {
    position: static;
    width: 100%;
    height: 400px;
  }
`;

export const HeaderText = styled.div`
  color: white;
  padding: 60px 30px;
  flex: 0 0 50%;

  h1 {
    font-size: 36px;
    margin: 30px 0;
    max-width: 600px;
    font-family: ${platform.headingFont};
  }

  p {
    font-size: 23px;
    margin: 30px 0;
    font-weight: 600;
    max-width: 600px;
  }

  @media (${laptopMediumMax}) {
    padding: 30px 15px;

    p {
      font-size: 18px;
    }
  }
`;

export const Logo = styled.img`
  width: 280px;
  height: 70px;
  display: block;
`;

export const ButtonStyle = styled.a`
  background-color: white;
  color: ${RED};
  padding: 15px 30px 15px;
  border-radius: 8px;
  font-weight: bold;
  font-size: 16px;
  text-decoration: none;
`;

export const ButtonLink = styled.a`
  display: inline-block;
  color: white;
  background-color: ${RED};
  padding: 15px 30px 15px;
  border-radius: 8px;
  font-weight: bold;
  align-self: flex-start;
`;

export const TextSection = styled.section`
  padding: 80px 0;
  background-color: #fbf7f7;

  @media (${laptopMediumMax}) {
    padding: 15px 0;
  }
`;

export const TextContent = styled.div<{ $reverseOnMobile?: boolean }>`
  display: flex;
  gap: 60px;
  margin: 0 auto;
  max-width: ${MAX_WIDTH}px;
  padding: 30px;
  color: ${({ theme }) => theme.textDark};

  & > div {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  h2,
  h3,
  p {
    margin: 15px 0;
  }

  h2 {
    font-size: 32px;
    font-family: ${platform.headingFont};
  }

  h3 {
    font-size: 24px;
  }

  h4 {
    color: ${RED};
    font-size: 30px;
    font-weight: normal;
  }

  p {
    font-size: 18px;
  }

  i {
    color: ${RED};
  }

  ul {
    padding-left: 20px;
  }

  li {
    font-size: 18px;
    margin: 10px 0;

    &::marker {
      color: ${RED};
    }
  }

  ${({ $reverseOnMobile }) => css`
    @media (${laptopMediumMax}) {
      flex-direction: ${$reverseOnMobile ? 'column-reverse' : 'column'};
      padding: 15px;
      gap: 15px;

      h3,
      h4 {
        font-size: 24px;
      }

      li,
      p {
        font-size: 18px;
      }
    }
  `}
`;

export const TextLink = styled.a`
  color: ${RED};
  text-decoration: underline;
`;

export const TextSectionImage = styled.div`
  width: 100%;
  max-width: 545px;
  height: 545px;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  @media (${laptopMediumMax}) {
    height: 300px;
  }
`;

export const FaqContainer = styled.div`
  margin: 0 auto;
  max-width: ${MAX_WIDTH}px;
  padding: 80px 30px;
  color: ${({ theme }) => theme.textDark};

  h2 {
    margin-bottom: 30px;
    font-family: ${platform.headingFont};
  }

  p,
  li {
    font-size: 18px;
  }

  a {
    color: ${RED};
    text-decoration: underline;

    &:hover {
      color: ${({ theme }) => theme.backgroundMid};
    }
  }

  @media (${laptopMediumMax}) {
    padding: 30px 15px;
  }
`;

export const Footer = styled.div`
  background: linear-gradient(
    90deg,
    rgba(232, 58, 79, 1) 0%,
    rgba(232, 58, 79, 1) 50%,
    rgba(251, 247, 247, 1) 50%,
    rgba(251, 247, 247, 1) 100%
  );

  @media (${laptopMediumMax}) {
    background: white;
  }
`;

export const FooterContent = styled.div`
  display: flex;
  margin: 0 auto;
  max-width: ${MAX_WIDTH}px;

  @media (${laptopMediumMax}) {
    flex-direction: column-reverse;
  }
`;

export const FooterLeft = styled.div`
  flex: 1;
  padding: 60px 30px;
  color: white;
  background-color: ${RED};

  h3 {
    margin: 30px 0;
  }

  h4 {
    font-size: 18px;
    margin: 5px 0;
  }

  a {
    text-decoration: underline;
  }

  @media (${laptopMediumMax}) {
    padding: 60px 15px;
  }
`;

export const FooterRight = styled.div`
  flex: 1;
  color: ${({ theme }) => theme.textDark};
  padding: 60px 30px;
  background-color: rgba(251, 247, 247, 1);

  h2 {
    font-family: ${platform.headingFont};
  }

  & > p {
    margin: 15px 0;
  }

  @media (${laptopMediumMax}) {
    padding: 60px 15px;
  }
`;
